import { useEffect, useState } from "react";
import { api, fmt, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MapPin, ScrollText, Sparkles, ChevronRight, User, Phone, MessageCircle,
  Home, Bus, Route, Wallet, Check, Clock, ExternalLink
} from "lucide-react";
import { Link } from "react-router-dom";

function PaymentBar({ steps = [] }) {
  const four = [...steps];
  while (four.length < 4) four.push({ label: ["Advance","2nd Payment","3rd Payment","Final Payment"][four.length], status: "pending" });
  return (
    <div className="mt-3">
      <div className="relative">
        <div className="grid grid-cols-4 gap-2 relative z-10">
          {four.map((s, i) => {
            const paid = (s.status || "").toLowerCase() === "paid";
            const active = (s.status || "").toLowerCase() === "active";
            const color = paid ? "bg-emerald-500 border-emerald-400 text-[hsl(224_60%_6%)]"
                        : active ? "bg-amber-500 border-amber-400 text-[hsl(224_60%_6%)] animate-pulse"
                        : "bg-[hsl(224_45%_12%)] border-border text-slate-400";
            return (
              <div key={i} className="text-center">
                <div className={`mx-auto w-9 h-9 rounded-full border-2 grid place-items-center font-bold ${color}`} data-testid={`pay-step-${i}`}>
                  {paid ? <Check className="w-4 h-4"/> : (active ? <Clock className="w-4 h-4"/> : (i + 1))}
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">{s.label || `Step ${i+1}`}</div>
                <div className="text-xs mt-0.5 text-slate-300">{s.due_date ? new Date(s.due_date).toLocaleDateString("en-GB") : "—"}</div>
                <div className={`text-sm font-mono mt-0.5 ${paid ? "text-emerald-300" : active ? "text-amber-300" : "text-slate-400"}`}>{fmt(s.amount || 0)}</div>
                {active && s.payment_link && (
                  <a href={s.payment_link} target="_blank" rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 underline underline-offset-2">
                    Pay now <ExternalLink className="w-3 h-3"/>
                  </a>
                )}
                {paid && s.paid_on && (
                  <div className="text-[10px] text-emerald-300/80 mt-0.5">Paid {new Date(s.paid_on).toLocaleDateString("en-GB")}</div>
                )}
              </div>
            );
          })}
        </div>
        <div className="absolute top-[18px] left-[12%] right-[12%] h-0.5 bg-border z-0"></div>
      </div>
    </div>
  );
}

function TripCard({ t, onAccept }) {
  const [expanded, setExpanded] = useState(!!t.client_tnc_accepted);
  const accepted = !!t.client_tnc_accepted;

  if (!accepted) {
    return (
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border" data-testid={`trip-card-${t.id}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80">Trip ID</div>
            <div className="font-mono text-amber-300 text-lg font-bold">{t.trip_id}</div>
          </div>
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">Awaiting T&C</Badge>
        </div>
        <div className="flex items-center gap-2 text-amber-400 mb-2">
          <ScrollText className="w-4 h-4"/>
          <div className="text-xs uppercase tracking-[0.2em]">Terms & Conditions</div>
        </div>
        <div className="text-sm text-slate-300 whitespace-pre-wrap bg-[hsl(224_40%_11%)] border border-border rounded-md p-3 max-h-56 overflow-y-auto">
          {t.terms_conditions || "No T&C provided by admin yet. Please contact your booking manager."}
        </div>
        <div className="flex justify-end pt-4">
          <Button
            onClick={() => onAccept(t.id)}
            disabled={!t.terms_conditions}
            data-testid={`accept-tnc-${t.id}`}
            className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">
            Accept & View Trip Details <ChevronRight className="w-4 h-4 ml-1"/>
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-[hsl(224_45%_9%)] border-border card-hover" data-testid={`trip-card-${t.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80">Trip ID</div>
          <div className="font-mono text-amber-300 text-lg font-bold">{t.trip_id}</div>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">T&C accepted</Badge>
          {t.gps_link && (
            <a href={t.gps_link} target="_blank" rel="noreferrer">
              <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/40 cursor-pointer inline-flex items-center gap-1">
                <MapPin className="w-3 h-3"/> Track live
              </Badge>
            </a>
          )}
        </div>
      </div>

      {/* Key figures */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
        <div className="rounded-md border border-border bg-[hsl(224_40%_11%)] p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Total trip cost</div>
          <div className="text-xl font-mono text-sky-300 mt-1">{fmt(t.total_trip_cost || t.trip_value || 0)}</div>
        </div>
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="text-[10px] uppercase text-emerald-300/80">Total paid</div>
          <div className="text-xl font-mono text-emerald-300 mt-1">{fmt(t.total_paid || 0)}</div>
        </div>
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="text-[10px] uppercase text-amber-300/80">Remaining payable</div>
          <div className="text-xl font-mono text-amber-300 mt-1">{fmt(t.remaining_payable || 0)}</div>
        </div>
      </div>

      {/* Assignment */}
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <div className="rounded-md border border-border bg-[hsl(224_40%_11%)] p-3">
          <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
            <Bus className="w-3 h-3"/> Assigned vehicle
          </div>
          <div className="text-sm mt-1">{t.assigned_vehicle_type || "—"}</div>
          <div className="font-mono text-sky-300 text-xs mt-0.5">{t.assigned_vehicle_reg || "—"}</div>
        </div>
        <div className="rounded-md border border-border bg-[hsl(224_40%_11%)] p-3">
          <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
            <User className="w-3 h-3"/> Assigned driver
          </div>
          <div className="text-sm mt-1">{t.assigned_driver_name || "—"}</div>
          <div className="font-mono text-emerald-300 text-xs mt-0.5">{t.assigned_driver_mobile || "—"}</div>
        </div>
      </div>

      {/* Itinerary */}
      {t.itinerary && (
        <div className="mt-4 rounded-md border border-border bg-[hsl(224_40%_11%)] p-3">
          <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground mb-1">
            <Route className="w-3 h-3"/> Itinerary
          </div>
          <div className="text-sm text-slate-300 whitespace-pre-wrap">{t.itinerary}</div>
        </div>
      )}

      {/* Payment status bar */}
      <div className="mt-4">
        <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground mb-1">
          <Wallet className="w-3 h-3"/> Payment status
        </div>
        <PaymentBar steps={t.payment_steps || []}/>
      </div>
    </Card>
  );
}

export default function ClientPortal() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [profile, setProfile] = useState(null);
  const [lottery, setLottery] = useState([]);

  async function load() {
    try {
      const [tRes, cRes, lRes] = await Promise.all([
        api.get("/trips"),
        api.get("/clients"),
        api.get("/lottery/my").catch(() => ({ data: [] })),
      ]);
      setTrips(tRes.data);
      const mine = (cRes.data || []).find((c) => c.email === user?.email || c.linked_user_id === user?.id);
      setProfile(mine || null);
      setLottery(lRes.data || []);
    } catch (e) {
      try { const tRes = await api.get("/trips"); setTrips(tRes.data); } catch {}
    }
  }
  useEffect(() => { load(); }, [user]);

  async function accept(tripId) {
    try {
      await api.post(`/trips/${tripId}/accept-tnc`);
      toast.success("Terms accepted — enjoy your trip!");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to accept");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-amber-300/80 mb-2">Client Portal</div>
        <h1 className="font-display text-4xl font-bold">Welcome, {profile?.name || user?.name}</h1>
        <p className="text-muted-foreground mt-2">Your bookings, payments and journey details.</p>
      </div>

      {/* Profile card */}
      {profile && (
        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border" data-testid="client-profile">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mb-3">My Profile</div>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground"><User className="w-3 h-3"/>Client name</div>
              <div className="text-sm mt-1 font-medium">{profile.name}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground"><Phone className="w-3 h-3"/>Mobile</div>
              <div className="text-sm mt-1 font-mono text-sky-300">{profile.mobile || "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground"><MessageCircle className="w-3 h-3"/>WhatsApp</div>
              <div className="text-sm mt-1 font-mono text-emerald-300">{profile.whatsapp || "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground"><Home className="w-3 h-3"/>Address</div>
              <div className="text-sm mt-1">{profile.address || "—"}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Lottery status banner */}
      {lottery.filter((e) => e.status === "approved").map((e) => (
        <Card key={e.id} className="p-5 bg-gradient-to-r from-emerald-500/15 to-transparent border-emerald-500/40" data-testid={`portal-lottery-approved-${e.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-300 mb-2"><Check className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.25em]">Lottery registration accepted</span></div>
              <div className="font-display text-2xl font-bold">Lottery ID · <span className="font-mono text-emerald-300">{e.lottery_id}</span></div>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl">{e.message}</p>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Valid till {e.valid_till?.slice(0, 10)}</Badge>
          </div>
        </Card>
      ))}
      {lottery.filter((e) => e.status === "pending").map((e) => (
        <Card key={e.id} className="p-4 bg-amber-500/10 border-amber-500/40" data-testid={`portal-lottery-pending-${e.id}`}>
          <div className="flex items-center gap-3 text-amber-200">
            <Clock className="w-4 h-4"/>
            <div className="text-sm">Your lottery registration is awaiting admin verification. We'll notify you here and via WhatsApp / email once approved.</div>
          </div>
        </Card>
      ))}

      {/* Lottery CTA */}
      <Card className="p-6 bg-gradient-to-r from-amber-500/10 to-transparent border-amber-500/30">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-amber-300 mb-1"><Sparkles className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.25em]">Loyalty programme</span></div>
            <h3 className="font-display text-2xl font-bold">Enter the annual international trip lottery</h3>
            <p className="text-sm text-slate-300 mt-1">Share 5-star reviews and hashtag posts. Trip voucher, flight ticket, mobile phone & more.</p>
          </div>
          <Link to="/lottery"><Button data-testid="client-lottery-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Enter now</Button></Link>
        </div>
      </Card>

      {trips.length === 0 ? (
        <Card className="p-8 bg-[hsl(224_45%_9%)] border-border text-center text-muted-foreground">No trips yet. Talk to your booking manager.</Card>
      ) : (
        <div className="space-y-4">
          {trips.map((t) => <TripCard key={t.id} t={t} onAccept={accept}/>)}
        </div>
      )}
    </div>
  );
}
